import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Shipments from '@/pages/Shipments';
import Map from '@/pages/Map';
import Connectors from '@/pages/Connectors';
import Alerts from '@/pages/Alerts';
import Devices from '@/pages/Devices';
import MobileScanner from '@/pages/MobileScanner';
import BulkActivation from '@/pages/BulkActivation';
import Analytics from '@/pages/Analytics';
import AlertRules from '@/pages/AlertRules';
import ProducerApp from '@/pages/ProducerApp';
import ShipperApp from '@/pages/ShipperApp';
import ReceiverApp from '@/pages/ReceiverApp';
import SupportDashboard from '@/pages/SupportDashboard';
import SilicaData from '@/pages/SilicaData';
import Receivers from '@/pages/Receivers';
import UserManagement from '@/pages/UserManagement';
import PublicTracking from '@/pages/PublicTracking';
import Layout from '@/components/Layout';
import '@/App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<Landing />} />
            
            {/* Public Tracking Page */}
            <Route path="/track/:sscc" element={<PublicTracking />} />
            
            {/* Login Page */}
            <Route path="/login" element={<Login />} />
            
            {/* Shipper Portal (v2 - has own login) */}
            <Route path="/shipper" element={<ShipperApp />} />
            
            {/* Producer Portal (legacy route - redirect to shipper) */}
            <Route
              path="/producer"
              element={
                <ProtectedRoute>
                  <ProducerApp />
                </ProtectedRoute>
              }
            />
            
            {/* Receiver Portal */}
            <Route
              path="/receiver"
              element={
                <ProtectedRoute>
                  <ReceiverApp />
                </ProtectedRoute>
              }
            />

            {/* Protected App Routes (Owner/Support/Admin view) */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="shipments" element={<Shipments />} />
              <Route path="map" element={<Map />} />
              <Route path="connectors" element={<Connectors />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="devices" element={<Devices />} />
              <Route path="scanner" element={<MobileScanner />} />
              <Route path="bulk-activate" element={<BulkActivation />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="alert-rules" element={<AlertRules />} />
              <Route path="silica" element={<SilicaData />} />
              <Route path="receivers" element={<Receivers />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="support" element={<SupportDashboard />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
